import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const eur = (n) => `€${(Number(n) || 0).toFixed(2).replace(".", ",")}`;

// Iter209eo — Modifica prezzo pratica (Admin/Supervisor) → status "standby" + email.
export function PriceEditDialog({ open, onClose, practice: p, onSaved }) {
  const [prof, setProf] = useState("");
  const [markup, setMarkup] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !p) return;
    setProf(String(Number(p.professional_price || 0).toFixed(2)));
    setMarkup(String(Number(p.client_markup || 0).toFixed(2)));
    setMessage("");
  }, [open, p]);

  if (!p) return null;
  const housing = Number(p.housing_fee) || 0;
  const oldTotal = Number(p.total_price) || 0;
  const newTotal = Math.round(((Number(prof) || 0) + (Number(markup) || 0) + housing) * 100) / 100;
  const diff = Math.round((newTotal - oldTotal) * 100) / 100;
  const answers = Array.isArray(p.pricing_answers) ? p.pricing_answers : [];
  const invalid = Number(prof) < 0 || Number(markup) < 0 || prof === "" || markup === "";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        professional_price: Number(prof),
        client_markup: Number(markup),
        total_price: newTotal,
        price_change_message: message.trim(),
      };
      const r = await api.patch(`/practices/${p._id || p.id}`, payload);
      toast.success("Prezzo aggiornato · pratica in standby");
      onSaved?.({ ...payload, total_price: r.data?.total_price ?? newTotal, status: "standby" });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio prezzo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="price-edit-dialog">
        <DialogHeader>
          <DialogTitle>Modifica prezzo</DialogTitle>
          <DialogDescription>{p.service_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="pe-prof">Prezzo professionista (€)</Label>
              <Input id="pe-prof" type="number" step="0.01" min="0" value={prof}
                onChange={(e) => setProf(e.target.value)} data-testid="price-edit-professional" />
            </div>
            <div>
              <Label htmlFor="pe-markup">Maggiorazione cliente (€)</Label>
              <Input id="pe-markup" type="number" step="0.01" min="0" value={markup}
                onChange={(e) => setMarkup(e.target.value)} data-testid="price-edit-markup" />
            </div>
          </div>
          {answers.length > 0 && (
            <div className="rounded-md border bg-slate-50 p-3" data-testid="price-edit-answers">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Prezzo flessibile</div>
              {answers.map((a, i) => (
                <div key={`${a.question_id}-${i}`} className="flex justify-between gap-3 text-xs py-0.5">
                  <span className="text-slate-600">{a.question_label || "Domanda"}: <strong className="text-slate-800">{a.answer_label || (a.answer_labels || []).join(", ") || "—"}</strong></span>
                  <span className="font-mono text-slate-700 shrink-0">{Number(a.delta_applied_eur) >= 0 ? "+" : ""}{eur(a.delta_applied_eur)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="rounded-md border p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Contributo Housetag</span><span className="font-mono">{eur(housing)}</span></div>
            <div className="flex justify-between font-semibold" data-testid="price-edit-total">
              <span>Totale</span>
              <span className="font-mono">
                {eur(oldTotal)} → {eur(newTotal)},{" "}
                <span className={diff > 0 ? "text-red-600" : diff < 0 ? "text-emerald-600" : "text-slate-500"} data-testid="price-edit-diff">
                  {diff >= 0 ? "+" : "−"}{eur(Math.abs(diff))}
                </span>
              </span>
            </div>
          </div>
          <div>
            <Label htmlFor="pe-msg">Messaggio *</Label>
            <Textarea id="pe-msg" rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Motivo della modifica, verrà inviato via email" data-testid="price-edit-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="price-edit-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving || invalid || !message.trim()} data-testid="price-edit-save">
            {saving ? "Salvo…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
